/**
 * createRenderer() — WebGLRenderer creation with sensible defaults.
 *
 * Encapsulates the boilerplate shared by TryonScene, ProductViewerScene,
 * and JewelryScene: pixel ratio capping, tone mapping, color space,
 * context loss/restore handlers.
 *
 * Usage:
 *   const THREE = await import('three');
 *   const { renderer, dispose, isContextLost } = createRenderer(THREE, {
 *     canvas,
 *     alpha: false,
 *   });
 */

import type { WebGLRenderer } from 'three';
import type { ThreeModule, RendererConfig, ManagedRenderer } from './types.js';

/**
 * Create a configured WebGLRenderer with context loss handling.
 *
 * @param THREE  - Dynamically imported Three.js module.
 * @param config - Renderer configuration.
 * @returns A ManagedRenderer with the renderer instance and lifecycle methods.
 */
export function createRenderer(
  THREE: ThreeModule,
  config: RendererConfig,
): ManagedRenderer {
  const {
    canvas,
    alpha,
    antialias = true,
    pixelRatio,
    toneMapping,
    toneMappingExposure = 1.0,
    premultipliedAlpha,
  } = config;

  // Build constructor options — only include premultipliedAlpha if explicitly set
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const opts: Record<string, any> = { canvas, antialias, alpha };
  if (premultipliedAlpha !== undefined) {
    opts.premultipliedAlpha = premultipliedAlpha;
  }

  const renderer = new THREE.WebGLRenderer(opts) as WebGLRenderer;

  // Pixel ratio: cap at provided value or 2 to avoid GPU pressure on HiDPI
  if (pixelRatio !== undefined) {
    renderer.setPixelRatio(pixelRatio);
  } else if (typeof window !== 'undefined') {
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }

  // Color space — always sRGB for correct PBR rendering
  (renderer as unknown as { outputColorSpace: string }).outputColorSpace =
    THREE.SRGBColorSpace as unknown as string;

  // Tone mapping
  if (toneMapping !== undefined) {
    (renderer as unknown as { toneMapping: number }).toneMapping = toneMapping;
  } else {
    (renderer as unknown as { toneMapping: number }).toneMapping =
      THREE.ACESFilmicToneMapping as unknown as number;
  }
  renderer.toneMappingExposure = toneMappingExposure;

  // Sorting enabled for correct render-order layering (occluder < glasses)
  renderer.sortObjects = true;

  // Shadows disabled by default — eyewear VTO doesn't need them and they
  // add ~10-15% GPU overhead per frame with no visible benefit.
  renderer.shadowMap.enabled = false;

  // ── Context loss/restore handlers ──────────────────────────────────────
  let contextLost = false;

  const onContextLost = (e: Event) => {
    e.preventDefault();
    contextLost = true;
  };

  const onContextRestored = () => {
    contextLost = false;
  };

  canvas.addEventListener('webglcontextlost', onContextLost, false);
  canvas.addEventListener('webglcontextrestored', onContextRestored, false);

  return {
    renderer,
    isContextLost: () => contextLost,
    dispose: () => {
      canvas.removeEventListener('webglcontextlost', onContextLost);
      canvas.removeEventListener('webglcontextrestored', onContextRestored);
      renderer.dispose();
    },
  };
}
